import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { CSS2DRenderer, CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer"
import { Earcut } from '../3dUtils/earcut'
import { getHouseFloors, getBuildingMaxFloors } from '../../utils/floors'

export default class HouseResourcesViewOpen {
  constructor(options) {
    const { element, history, sellControlMap, onSwitchBuild, onRoomDetail, minBaseHeight} = options
    this._element = element // canvas 元素
    this._history = history
    this._sellControlMap = sellControlMap
    this.onSwitchBuild = onSwitchBuild
    this.onRoomDetail = onRoomDetail
    this.buildingNumberMap = new Map()
    this.buildingGroup = []
    this.originalPosition = null
    this.center = new THREE.Vector3(0, 0, 0)
    this.buildingPositionMap = new Map()
    this.roomMeshMap = new Map()
    this.selectBuild = null
    this.currentSelectRoom = null
    this.mouseDownPosition = new THREE.Vector2(), // 记录鼠标按下位置
    this.mouseUpPosition = new THREE.Vector2() // 记录鼠标松开位置
    this.minBaseHeight = minBaseHeight

    this.houseTopInfoMap = new Map();
    this.selectBuildTopGroup = null;
  }

  init () {
    this._initRender()
    this._initScene()
    this._initCamera()
    this._initLight()
    this._initControl()
    this._addBasePlane()
    this._addSkyBox()
    this._initMouseClick()
    this._render()
    this.lon = this._history.destLng
    this.lat = this._history.destLat

    this.createBuildings(this._history)
    this.initSellControl()
    // this._scene.add(this.fieldMesh)
  }

  _render() {
    this._renderer.render(this._scene, this._camera)
    this._control.update()
    this._labelRenderer.render(this._scene, this._camera)
    if(this._compass) {
      // 获取相机位置和目标点方向
      const direction = new THREE.Vector3();
      this._camera.getWorldDirection(direction);
      
      // 计算水平面投影后的角度（XZ平面）
      const angle = Math.atan2(-direction.z, direction.x);
      const degrees = THREE.MathUtils.radToDeg(angle) - 90; // +90度修正图片初始方向
      
      this._compass.style.transform = `rotate(${degrees}deg)`;
    }
    requestAnimationFrame(() => {
      this._render()
    })
  }

  _initRender() {
    let renderer = new THREE.WebGLRenderer({
      canvas: this._element,
      antialias: true, // 抗锯齿
      // logarithmicDepthBuffer: true, // 是否使用对数深度缓存
      alpha: true // 透明度
    })
    renderer.setSize(window.innerWidth, window.innerHeight)
    renderer.setPixelRatio(window.devicePixelRatio)
    renderer.setClearColor(0xb9d3ff, 1)

    this._renderer = renderer

    // CSS2DRenderer
    let labelRenderer = new CSS2DRenderer()
    labelRenderer.setSize(window.innerWidth, window.innerHeight)
    labelRenderer.domElement.style.position = 'absolute'
    labelRenderer.domElement.style.top = '0'
    labelRenderer.domElement.style.pointerEvents = 'none'
    document.body.appendChild(labelRenderer.domElement)
    this._labelRenderer = labelRenderer
  }

  _initScene() {
    let scene = new THREE.Scene()
    scene.background = new THREE.Color(0x222222)
    this._scene = scene
  }

  _initCamera() {
    let camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      5,
      5000
    )
    camera.position.set(0, 400, 400)
    camera.lookAt(this._scene.position)
    this._camera = camera
  }

  _initLight() {
    let light = new THREE.AmbientLight(0x666666, 0.8)
    this._scene.add(light)
  }

  _initControl() {
    const control = new OrbitControls(this._camera, this._renderer.domElement)
    control.enableDamping = true
    control.enableZoom = true
    control.enablePan = true
    control.autoRotate = false
    control.autoRotateSpeed = 0.5
    control.minPolarAngle = 0
    control.maxPolarAngle = Math.PI / 2 - 0.1
    control.minDistance = 1
    control.maxDistance = 10000
    this._control = control

    this._compass = document.createElement('div');
    this._compass.style.position = 'fixed';
    this._compass.style.top = '20px';
    this._compass.style.left = '20px';
    this._compass.style.width = '100px';
    this._compass.style.height = '100px';
    this._compass.style.background = 'url(/images/compass.png) no-repeat center/contain';
    document.body.appendChild(this._compass);
  }

  // 天空盒
  _addSkyBox() {
    this._scene.background = new THREE.CubeTextureLoader()
      .setPath('/images/')
      .load([
        'center.png',
        'center.png',
        'top.png',
        'down.png',
        'center.png',
        'center.png'
      ])
  }

  // 地平面
  _addBasePlane() {
    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    shape.arc(0, 0, 1024, 0, Math.PI * 2, false); // 半径为1的圆
    const geometry = new THREE.ShapeGeometry(shape);
    // const planeGeometry = new THREE.PlaneGeometry(1024, 1024)
    // const groundTexture = new THREE.TextureLoader().load(this._baseMap)
    const planeMaterial = new THREE.MeshLambertMaterial({
      color: 0x9FB6E0,
      emissive: 0x9FB6E0, // 可选：设置自发光颜色，让材质在没有光照时也能有一定的亮度  
      emissiveIntensity: 0.3, // 可选：设置自发光的强度  ,
      depthTest: true,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    })
    const planeMesh = new THREE.Mesh(geometry, planeMaterial)
    planeMesh.name = '地图平面'
    planeMesh.rotateX(-Math.PI / 2)
    planeMesh.receiveShadow = true
    planeMesh.castShadow = true
    this._scene.add(planeMesh)
  }

  _initMouseClick() {
    const onMouseClick = (event) => {
      if (this.inSingleMode) {
        this.clickRoomListener(event)
      } else{
        this.clickBuildingListener(event)
      }
    };

    const onMouseDown = (event) => {
      this.mouseDownPosition.set(event.clientX, event.clientY);
    }

    const onMouseUp = (event) => {
      this.mouseUpPosition.set(event.clientX, event.clientY);
      const deltaX = this.mouseUpPosition.x - this.mouseDownPosition.x;
      const deltaY = this.mouseUpPosition.y - this.mouseDownPosition.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      if (distance < 5) {
        // 距离很小，认为是点击事件
        onMouseClick(event);
      } else {
        // 距离较大，认为是拖拽事件
        return
      }
    }

    // window.addEventListener('click', onMouseClick);
    this._element.addEventListener('pointerdown', onMouseDown);
    this._element.addEventListener('pointerup', onMouseUp);
  }

  clickBuildingListener(event){
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    let x, y;
    if (event.touches) {
      // 触摸事件
      x = event.touches[0].clientX;
      y = event.touches[0].clientY;
    } else {
      // 鼠标事件
      x = event.clientX;
      y = event.clientY;
    }
    mouse.x = (x / window.innerWidth) * 2 - 1;
    mouse.y = -(y / window.innerHeight) * 2 + 1;

    raycaster.setFromCamera(mouse, this._camera);
    const intersects = raycaster.intersectObjects(this._scene.children, true);
    if (intersects.length > 0) {
      const targetObject = intersects[0].object;
      // 查找该对象所属的 Group
      let selectedGroup = targetObject;
      while (selectedGroup && !(selectedGroup instanceof THREE.Group)) {
        selectedGroup = selectedGroup.parent;
      }
      // 如果找到 Group 并且是目标建筑物
      if (selectedGroup && selectedGroup.name.startsWith('Building')) {
        this.selectBuild == selectedGroup
        this.highlightBuilding(this.buildingGroup, selectedGroup);
      }
    }
  }

  highlightBuilding(buildingGroup, selectedBuilding) {
    let totalFloor = 0;
    var buildNameArr = selectedBuilding.name.split('-')
    if (buildNameArr.length > 0) {
      var buildNumber = buildNameArr[1]
      totalFloor = Number(buildNameArr[2])
      this.onSwitchBuild(buildNumber)
      const houseTopInfo = this.houseTopInfoMap.get(decodeURIComponent(selectedBuilding.name.split('-')[1]).replace('%2D', '-'))
      this.addHouseText(houseTopInfo)
    }

    this.selectBuildGroup = selectedBuilding
    const buildingPosition = this.buildingPositionMap.get(selectedBuilding);
    const cameraPosition = new THREE.Vector3(buildingPosition.x, 0, buildingPosition.z);
    // 切换相机视角到该建筑物
    this._camera.position.set(cameraPosition.x, 0, cameraPosition.z + 250);
    this._camera.fov = 30; // 设置视角为 30 度（默认通常是 50 或 75）
    this._control.target.copy(cameraPosition);

    this.inSingleMode = true;
    let buildingNumber = decodeURIComponent(selectedBuilding.name.split('-')[1]).replace('%2D', '-')
    buildingGroup.forEach(group => {
      const isSelected = group === selectedBuilding;
      group.children.forEach(child => {
        if (child.material) {
          // 设置材质透明度
          child.material.transparent = true;
          child.material.opacity = isSelected ? 1 : 0;
        }
        if (!isSelected) {
          child.renderOrder = 1
        }
        else {
          child.renderOrder = 0
        }
        if (child.type === 'Object3D') {
          if (isSelected) {
            child.element.innerHTML = `
            <div class='three-d-label-container'>
              <div class='three-d-label'>${buildingNumber}#  |  共${totalFloor}层 </div>
              <div class='three-d-label-btn'>
                <img src="/images/quit.png" alt="">
              </div>
            </div>
            `
            child.element.style.pointerEvents = 'auto'
            child.element.addEventListener('click', () => {
              this.resetHightBuilding(this.buildingGroup, selectedBuilding);
            })
          }
          else {
            child.element.hidden = true
          }
        }
      });
    })
  }

  resetHightBuilding(buildingGroup, selectedBuilding) {
    this._camera.position.copy(this.originalPosition); // 恢复初始相机位置
    this._control.target.copy(this.center); // 恢复相机的目标点
    buildingGroup.forEach(group => {
      group.children.forEach(child => {
        if (child.material) {
          // 设置材质透明度
          child.material.transparent = false;
          child.material.opacity = 1; // 高亮选中，其他虚化
        }
        if (child.type === 'Object3D') {
          if (group === selectedBuilding) {
            let buildingNumber = decodeURIComponent(group.name.split("-")[1]).replace('%2D', '-')
            child.element.innerHTML = `<div class="three-d-label">${buildingNumber}#</div>`
          }
          child.element.hidden = false
        }
      });
    });
    if (this.currentSelectRoom) {
      const sellType = this.currentSelectRoom.name.split('_')[5]
      this.updateRoomColor(this.currentSelectRoom,sellType)
      this.currentSelectRoom = null
    }
    this.inSingleMode = false;
    this.selectBuild = null
    if(this.selectBuildTopGroup){
      this.removeMeshGroup(this.selectBuildTopGroup)
    }
    // this.onCancelBuildSelect();
  }

  clickRoomListener(event){
    const raycaster = new THREE.Raycaster()
    const mouse = new THREE.Vector2()
    let x, y
    if (event.touches) {
      // 触摸事件
      x = event.touches[0].clientX
      y = event.touches[0].clientY
    } else {
      // 鼠标事件
      x = event.clientX
      y = event.clientY
    }
    mouse.x = (x / window.innerWidth) * 2 - 1
    mouse.y = -(y / window.innerHeight) * 2 + 1

    raycaster.setFromCamera(mouse, this._camera)
    const intersects = raycaster.intersectObjects(this._scene.children, true).filter(intersect => 
      intersect.object.material.opacity > 0 && 
      intersect.object.visible
    )
    if (intersects.length > 0) {
      const targetObject = intersects[0].object;
      // 查找该对象所属的 Group
      let selectedMesh = targetObject
      if (selectedMesh && selectedMesh.name.startsWith('Room')) {
        this.switchSelectRoom(selectedMesh)
      }
    }
  }

  switchSelectRoom (selectedMesh) {
    if(selectedMesh) {
      if (this.currentSelectRoom) {
        const sellType = this.currentSelectRoom.name.split('_')[5]
        this.updateRoomColor(this.currentSelectRoom,sellType)
      }
      this.updateRoomColor(selectedMesh,'select')
      this.currentSelectRoom = selectedMesh
      const roomKey = selectedMesh.name.replace(/^Room_/, "")
      this.onRoomDetail(roomKey,'3D')
    }
    else{
      const sellType = this.currentSelectRoom.name.split('_')[5]
      this.updateRoomColor(this.currentSelectRoom,sellType)
      this.currentSelectRoom = null
    }
  }
  switchSelectRoomByTable (roomKey) {
    const selectedMesh = this.roomMeshMap.get(roomKey)
    if(selectedMesh) {
      if (this.currentSelectRoom) {
        const sellType = this.currentSelectRoom.name.split('_')[5]
        this.updateRoomColor(this.currentSelectRoom,sellType)
      }
      this.updateRoomColor(selectedMesh,'select')
      this.currentSelectRoom = selectedMesh
    }
  }

  createBuildings(historyDetail) {
    // 初始化x和z的最小最大值
    let minX = Infinity, maxX = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;
    //设置中心点(观测楼第一个点)
    this.lon = historyDetail.destLng;
    this.lat = historyDetail.destLat;

    this.originalPosition = this._camera.position.clone();

    //添加楼
    if (historyDetail.barrierReqList && historyDetail.barrierReqList.length > 0) {
      for (var index = 0; index < historyDetail.barrierReqList.length; index++) {
        var building = historyDetail.barrierReqList[index];
        let houseRoomList = building.houseList;
        let buildGroup = new THREE.Group()
        if(houseRoomList.length > 0){
          for (var houseIndex = 0; houseIndex < houseRoomList.length; houseIndex++) {
            var houseRoom = houseRoomList[houseIndex];
            this.addBuildingByHouse(buildGroup, houseRoom, building);
          }
          let encodeBuildNumber = encodeURIComponent(building.buildingNumber).replace('-','%2D')
          buildGroup.name = `Building-${encodeBuildNumber}-${getBuildingMaxFloors(building)}`;
          this._scene.add(buildGroup);
  
          let buildPoints = this.transformCoordinate(this.transFormPositions(building.positionList))
          const sum = [0, 0, 0];
          buildPoints.forEach(point => {
            sum[0] += point[0];
            sum[2] += -point[1];
          })
  
          this.buildingPositionMap.set(buildGroup, new THREE.Vector3(sum[0] / buildPoints.length, (getBuildingMaxFloors(building) + 2) * building.floorHeight, sum[2] / buildPoints.length))

          const div2 = document.createElement('div');
          div2.innerHTML = `<div class="three-d-label">${building.buildingNumber}#</div>`
          const label2 = new CSS2DObject(div2);
          label2.position.set(sum[0] / buildPoints.length, (getBuildingMaxFloors(building) + 2) * building.floorHeight, sum[2] / buildPoints.length);
          buildGroup.add(label2);
          this.buildingNumberMap.set(encodeBuildNumber, buildGroup)
        } else {
          this.addBuildingByBuilding(buildGroup, building);
          this._scene.add(buildGroup);
        }
        this.buildingGroup.push(buildGroup);
        const boundingBox = new THREE.Box3().setFromObject(buildGroup);
        if (boundingBox.min.x < minX) minX = boundingBox.min.x;
        if (boundingBox.max.x > maxX) maxX = boundingBox.max.x;

        // 更新z轴的最小最大值
        if (boundingBox.min.z < minZ) minZ = boundingBox.min.z;
        if (boundingBox.max.z > maxZ) maxZ = boundingBox.max.z;
      }
      if (this._history.roadList != null && this._history.roadList.length > 0) {
        this.createRoads(this._history.roadList)
      } else {
        this.createDefaultRoad(minX, maxX, minZ, maxZ)
      }
    }
  }

  createDefaultRoad(minX, maxX, minZ, maxZ) {
    const group = new THREE.Group();
    const materialArr = new THREE.MeshLambertMaterial({
      color: 0xFFFFFF,
      side: THREE.BackSide,
      emissive: 0xFFFFFF, // 可选：设置自发光颜色，让材质在没有光照时也能有一定的亮度  
      emissiveIntensity: 0.5, // 可选：设置自发光的强度
    })
    var verticesA = [];
    var roadAPonit1 = new THREE.Vector3(minX - 30, 0.1, minZ - 60)
    var roadAPonit2 = new THREE.Vector3(minX - 10, 0.1, minZ - 60)
    var roadAPonit3 = new THREE.Vector3(minX - 10, 0.1, maxZ + 60)
    var roadAPonit4 = new THREE.Vector3(minX - 30, 0.1, maxZ + 60)
    verticesA.push(roadAPonit1);
    verticesA.push(roadAPonit2);
    verticesA.push(roadAPonit3);
    verticesA.push(roadAPonit4);
    var facesA = []
    facesA.push(new THREE.Face3(0, 1, 2));
    facesA.push(new THREE.Face3(0, 2, 3));
    var geometryA = new THREE.Geometry();
    geometryA.vertices = verticesA;
    geometryA.faces = facesA;
    geometryA.computeFaceNormals();
    const meshA = new THREE.Mesh(geometryA, materialArr)
    meshA.castShadow = true
    meshA.receiveShadow = true
    group.add(meshA);

    var verticesB = [];
    var roadBPonit1 = new THREE.Vector3(minX - 60, 0.1, minZ - 30)
    var roadBPonit2 = new THREE.Vector3(maxX + 60, 0.1, minZ - 30)
    var roadBPonit3 = new THREE.Vector3(maxX + 60, 0.1, minZ - 10)
    var roadBPonit4 = new THREE.Vector3(minX - 60, 0.1, minZ - 10)
    verticesB.push(roadBPonit1);
    verticesB.push(roadBPonit2);
    verticesB.push(roadBPonit3);
    verticesB.push(roadBPonit4);
    var facesB = []
    facesB.push(new THREE.Face3(0, 1, 2));
    facesB.push(new THREE.Face3(0, 2, 3));
    var geometryB = new THREE.Geometry();
    geometryB.vertices = verticesB;
    geometryB.faces = facesB;
    geometryB.computeFaceNormals();
    const roadMeshB = new THREE.Mesh(geometryB, materialArr)
    roadMeshB.castShadow = true
    roadMeshB.receiveShadow = true
    group.add(roadMeshB);

    var verticesC = [];
    var roadCPonit1 = new THREE.Vector3(maxX + 10, 0.1, minZ - 60)
    var roadCPonit2 = new THREE.Vector3(maxX + 30, 0.1, minZ - 60)
    var roadCPonit3 = new THREE.Vector3(maxX + 30, 0.1, maxZ + 60)
    var roadCPonit4 = new THREE.Vector3(maxX + 10, 0.1, maxZ + 60)
    verticesC.push(roadCPonit1);
    verticesC.push(roadCPonit2);
    verticesC.push(roadCPonit3);
    verticesC.push(roadCPonit4);
    var facesC = []
    facesC.push(new THREE.Face3(0, 1, 2));
    facesC.push(new THREE.Face3(0, 2, 3));
    var geometryC = new THREE.Geometry();
    geometryC.vertices = verticesC;
    geometryC.faces = facesC;
    geometryC.computeFaceNormals();
    const roadMeshC = new THREE.Mesh(geometryC, materialArr)
    roadMeshC.castShadow = true
    roadMeshC.receiveShadow = true
    group.add(roadMeshC);

    var verticesD = [];
    var roadDPonit1 = new THREE.Vector3(minX - 60, 0.1, maxZ + 10)
    var roadDPonit2 = new THREE.Vector3(maxX + 60, 0.1, maxZ + 10)
    var roadDPonit3 = new THREE.Vector3(maxX + 60, 0.1, maxZ + 30)
    var roadDPonit4 = new THREE.Vector3(minX - 60, 0.1, maxZ + 30)
    verticesD.push(roadDPonit1);
    verticesD.push(roadDPonit2);
    verticesD.push(roadDPonit3);
    verticesD.push(roadDPonit4);
    var facesD = []
    facesD.push(new THREE.Face3(0, 1, 2));
    facesD.push(new THREE.Face3(0, 2, 3));
    var geometryD = new THREE.Geometry();
    geometryD.vertices = verticesD;
    geometryD.faces = facesD;
    geometryD.computeFaceNormals();
    const roadMeshD = new THREE.Mesh(geometryD, materialArr)
    roadMeshD.castShadow = true
    roadMeshD.receiveShadow = true
    group.add(roadMeshD);


    //小区地面
    var verticesE = [];
    var communityPonit1 = new THREE.Vector3(minX - 10, 0.1, minZ - 10)
    var communityPonit2 = new THREE.Vector3(maxX + 10, 0.1, minZ - 10)
    var communityPonit3 = new THREE.Vector3(maxX + 10, 0.1, maxZ + 10)
    var communityPonit4 = new THREE.Vector3(minX - 10, 0.1, maxZ + 10)
    verticesE.push(communityPonit1)
    verticesE.push(communityPonit2)
    verticesE.push(communityPonit3)
    verticesE.push(communityPonit4)
    var facesE = []
    facesE.push(new THREE.Face3(0, 1, 2));
    facesE.push(new THREE.Face3(0, 2, 3));

    var geometryE = new THREE.Geometry();
    geometryE.vertices = verticesE;
    geometryE.faces = facesE;
    geometryE.computeFaceNormals();

    const materialArr2 = new THREE.MeshLambertMaterial({
      color: 0x8395B7,
      side: THREE.BackSide,
      emissive: 0x8395B7, // 可选：设置自发光颜色，让材质在没有光照时也能有一定的亮度  
      emissiveIntensity: 0.5, // 可选：设置自发光的强度  
    })

    const meshE = new THREE.Mesh(geometryE, materialArr2)
    meshE.castShadow = true
    meshE.receiveShadow = true
    group.add(meshE);

    this._scene.add(group)
  }
  
  createRoads(roadList) {
    let roadGroup = new THREE.Group()
    roadList.forEach(road => {
      let roadPoints = this.transformCoordinate(this.transFormPositions(road.geometryLoc))
      roadPoints = roadPoints.map(point => {
        return [point[0], 0.5, -point[1]]
      })
      let vertices = [];
      let faces = [];
      let data = [];
      for (let i = 0; i < roadPoints.length; i++) {
        vertices.push(
          new THREE.Vector3(roadPoints[i][0], roadPoints[i][1], roadPoints[i][2])
        )
        data.push(roadPoints[i][0], roadPoints[i][2])
      }
      var triangles = Earcut.triangulate(data);
      if (triangles && triangles.length != 0) {
        for (let i = 0; i < triangles.length; i++) {
          var tlength = triangles.length
          if (i % 3 == 0 && i < tlength - 2) {
            //底部的三角面
            let face1 = new THREE.Face3(
              triangles[i],
              triangles[i + 1],
              triangles[i + 2]
            )
            //纹理编号
            faces.push(face1)
          }
        }
      }
      var geometry = new THREE.Geometry()
      geometry.vertices = vertices
      geometry.faces = faces
      geometry.computeFaceNormals() //自动计算法向量
      let material = new THREE.MeshLambertMaterial({
        color: 0xFFFFFF,
        side: THREE.BackSide,
        emissive: 0xFFFFFF, // 可选：设置自发光颜色，让材质在没有光照时也能有一定的亮度  
        emissiveIntensity: 0.4, // 可选：设置自发光的强度
      })
      let mesh = new THREE.Mesh(geometry, material)
      roadGroup.add(mesh)
    })
    this._scene.add(roadGroup)
  }


  transformCoordinate(coordinates) {
    const center = this.lonlat2WebMercator(this.lon, this.lat);

    // web 墨卡托投影有变形
    const p = Math.cos(this.degreesToRadians(this.lat));
    let newCoordinates = coordinates.map(coordinate => {
      let newRing = [];
      const newCoordinate = this.lonlat2WebMercator(coordinate[0], coordinate[1]);

      const x = Math.round((newCoordinate[0] - center[0]) * p * 100) / 100;
      const y = Math.round((newCoordinate[1] - center[1]) * p * 100) / 100;
      newRing.push(x);
      newRing.push(y);
      return newRing;
    })
    return newCoordinates;
  }

  addBuildingByHouse(buildGroup, houseRoom, building) {
    let totalFloor = getHouseFloors(houseRoom, building)
    let floorHeight = building.floorHeight
    let points = this.transformCoordinate(this.transFormPositions(houseRoom.positionList));
    points = points.map(point => {
      return [point[0], 0, -point[1]]
    })
    this.addBuildingBaseHeight(buildGroup, building, points)
    for (let i = 0; i < totalFloor; i++) {
      this.createBuildingFloorByHouse(buildGroup, building.buildingNumber,houseRoom,points, floorHeight, i, building.baseHeight)
    }

    let houseTopInfo = {
      baseHeight:0,
      floorHeight:0,
      floor:0,
      totalHeight:0,
      houseRoom:[]
    }
    if(this.houseTopInfoMap.has(building.buildingNumber)){
      houseTopInfo = this.houseTopInfoMap.get(building.buildingNumber)
    }

    houseTopInfo.baseHeight = building.baseHeight
    houseTopInfo.totalHeight = building.totalHeight
    houseTopInfo.floorHeight = building.floorHeight
    houseTopInfo.totalFloor = building.totalFloor
    houseTopInfo.houseRoom.push(houseRoom)
    this.houseTopInfoMap.set(building.buildingNumber, houseTopInfo)
  }

  addBuildingByBuilding(buildGroup, building) {
    let totalFloor = building.totalFloor
    let floorHeight = building.floorHeight
    let points = this.transformCoordinate(this.transFormPositions(building.positionList));
    points = points.map(point => {
      return [point[0], 0, -point[1]]
    })
    this.addBuildingBaseHeight(buildGroup, building, points)
    for (let i = 0; i < totalFloor; i++) {
      this.createBuildingFloorByBuilding(buildGroup, points, floorHeight, i, building.baseHeight)
    }
  }

  addBuildingBaseHeight (buildGroup, building, points) {
    if (this.minBaseHeight != building.baseHeight) {
      let finalBaseHeight = 0;
      if (this.minBaseHeight < 0)
      {
        finalBaseHeight = building.baseHeight - this.minBaseHeight
      } else {
        finalBaseHeight = building.baseHeight
      }
      const geometry = this.getGeometry(points, 0, finalBaseHeight)
      const materialArr = new THREE.MeshLambertMaterial({
        color: 0x4F4F4F,
        emissive: 0x4F4F4F, // 可选：设置自发光颜色，让材质在没有光照时也能有一定的亮度  
        side: THREE.BackSide,
        depthTest: true,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      })
      const mesh = new THREE.Mesh(geometry, materialArr)
      mesh.castShadow = true
      mesh.receiveShadow = true
      buildGroup.add(mesh)
    }
  }

  addHouseText (houseTopInfo) {
    let finalBaseHeight = 0
    if (this.minBaseHeight < 0) {
      finalBaseHeight = houseTopInfo.baseHeight - this.minBaseHeight
    } 
    else {
      finalBaseHeight = houseTopInfo.baseHeight 
    }
    let houseTextGroup = new THREE.Group()
    houseTopInfo.houseRoom.forEach(houseRoom => {
      let points = this.transformCoordinate(this.transFormPositions(houseRoom.positionList));
      points = points.map(point => {
        return [point[0], 0, -point[1]]
      })
      let rectPoints = this.computeMinBoundingRectangle(points)
      rectPoints = this.orderRectangleVertices(rectPoints)
      rectPoints.push(rectPoints[0])
      // 本单元单独填了层数时文字要跟着降下来，否则会浮在没盖的楼层上
      const houseFloors = getHouseFloors(houseRoom, houseTopInfo)
      const houseTotalHeight = houseTopInfo.totalHeight - (houseTopInfo.totalFloor - houseFloors) * houseTopInfo.floorHeight
      //添加顶部文本
      this.addHouseTopText(houseTextGroup, rectPoints, houseRoom.unit, houseRoom.room, finalBaseHeight + houseTotalHeight)
      //添加墙面文本
      this.addHouseWallText(houseTextGroup, rectPoints, finalBaseHeight, houseTopInfo.floorHeight, houseFloors, houseRoom.room)
    })
    this._scene.add(houseTextGroup)
    this.selectBuildTopGroup = houseTextGroup
  }

  addHouseTopText(houseTextGroup, points, unit, room, totalHeight){
    let vertices = []
    for (let i = 0; i < points.length; i++) {
      vertices.push(
        new THREE.Vector3(points[i][0], totalHeight, points[i][1])
      )
    }
    const width = vertices[0].distanceTo(vertices[1])
    const height = vertices[1].distanceTo(vertices[2])

    let faces = []
    faces.push(new THREE.Face3(0, 1, 2))
    faces.push(new THREE.Face3(3, 0, 2))
    var geometry = new THREE.Geometry()
    geometry.vertices = vertices
    geometry.faces = faces
    geometry.computeFaceNormals() //自动计算法向量
    geometry.faceVertexUvs = [
      [
        [
          new THREE.Vector2(0, 1),
          new THREE.Vector2(1, 1),
          new THREE.Vector2(1, 0),
        ],
        [
          new THREE.Vector2(0, 0),
          new THREE.Vector2(0, 1) ,
          new THREE.Vector2(1, 0),
        ],
      ],
    ]
    const materialArr = new THREE.MeshLambertMaterial({
      map: this.getBuildingText(width, height, `${unit}-${Number(room)}`),
      transparent: true, // 启用透明
      side: THREE.DoubleSide,
      alphaTest: 0.01,   // 关键：丢弃低alpha像素
    })      
    const mesh = new THREE.Mesh(geometry, materialArr)
    mesh.castShadow = true
    mesh.receiveShadow = true
    houseTextGroup.add(mesh)
  }

  addHouseWallText (houseTextGroup, rectPoints, baseHeight, floorHeight, floor, room) {
    let newRoom =  room
    if(Number(room) < 10){
      newRoom = "0" + Number(room);
    }
    for(let i = 0; i < floor; i++){
      let southWallPoints = []
      southWallPoints.push([rectPoints[3][0], baseHeight + floorHeight * i, rectPoints[3][1]])
      southWallPoints.push([rectPoints[2][0], baseHeight + floorHeight * i, rectPoints[2][1]])
      southWallPoints.push([rectPoints[2][0], baseHeight + floorHeight * (i + 1), rectPoints[2][1]])
      southWallPoints.push([rectPoints[3][0], baseHeight + floorHeight * (i + 1), rectPoints[3][1]])
      this.createTextWall(houseTextGroup, southWallPoints, i, newRoom)

      let eastWallPoints = []
      eastWallPoints.push([rectPoints[2][0], baseHeight + floorHeight * i, rectPoints[2][1]])
      eastWallPoints.push([rectPoints[1][0], baseHeight + floorHeight * i, rectPoints[1][1]])
      eastWallPoints.push([rectPoints[1][0], baseHeight + floorHeight * (i + 1), rectPoints[1][1]])
      eastWallPoints.push([rectPoints[2][0], baseHeight + floorHeight * (i + 1), rectPoints[2][1]])
      this.createTextWall(houseTextGroup, eastWallPoints, i, newRoom)

      let northWallPoints = []
      northWallPoints.push([rectPoints[1][0], baseHeight + floorHeight * i, rectPoints[1][1]])
      northWallPoints.push([rectPoints[0][0], baseHeight + floorHeight * i, rectPoints[0][1]])
      northWallPoints.push([rectPoints[0][0], baseHeight + floorHeight * (i + 1), rectPoints[0][1]])
      northWallPoints.push([rectPoints[1][0], baseHeight + floorHeight * (i + 1), rectPoints[1][1]])
      this.createTextWall(houseTextGroup, northWallPoints, i, newRoom)

      let westWallPoints = []
      westWallPoints.push([rectPoints[0][0], baseHeight + floorHeight * i, rectPoints[0][1]])
      westWallPoints.push([rectPoints[3][0], baseHeight + floorHeight * i, rectPoints[3][1]])
      westWallPoints.push([rectPoints[3][0], baseHeight + floorHeight * (i + 1), rectPoints[3][1]])
      westWallPoints.push([rectPoints[0][0], baseHeight + floorHeight * (i + 1), rectPoints[0][1]])
      this.createTextWall(houseTextGroup, westWallPoints, i, newRoom)

    }
  }

  createTextWall (houseTextGroup, wallPoints, floor, room) {
    let vertices = []
      for (let i = 0; i < wallPoints.length; i++) {
        vertices.push(
          new THREE.Vector3(wallPoints[i][0], wallPoints[i][1], wallPoints[i][2])
        )
      }
      let faces = []
      faces.push(new THREE.Face3(0, 1, 2))
      faces.push(new THREE.Face3(3, 0, 2))

      const width = vertices[0].distanceTo(vertices[1])
      const height = vertices[1].distanceTo(vertices[2])
      
      let geometry = new THREE.Geometry()
      geometry.vertices = vertices
      geometry.faces = faces
      geometry.computeFaceNormals() //自动计算法向量
      geometry.faceVertexUvs = [
        [
          [
            new THREE.Vector2(0, 0),
            new THREE.Vector2(1, 0),
            new THREE.Vector2(1, 1),
          ],
          [
            new THREE.Vector2(0, 1),
            new THREE.Vector2(0, 0) ,
            new THREE.Vector2(1, 1),
          ],
        ],
      ]
      const materialArr = new THREE.MeshLambertMaterial({
        map: this.getBuildingText(width, height, `${floor + 1}${room}`),
        transparent: true, // 启用透明
        side: THREE.DoubleSide,
        alphaTest: 0.01,   // 关键：丢弃低alpha像素
      })
      const mesh = new THREE.Mesh(geometry, materialArr)
      houseTextGroup.add(mesh)
  }

  convexHull(points) {
    if (points.length <= 3) return points.slice();
    
    // 按x坐标排序，x相同则按y排序
    points.sort((a, b) => a.x - b.x || a.y - b.y);
    
    const lower = [];
    for (const p of points) {
        while (lower.length >= 2 && this.cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
            lower.pop();
        }
        lower.push(p);
    }
    
    const upper = [];
    for (let i = points.length - 1; i >= 0; i--) {
        const p = points[i];
        while (upper.length >= 2 && this.cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
            upper.pop();
        }
        upper.push(p);
    }
    
    lower.pop(); // 移除首尾重复点
    upper.pop();
    return lower.concat(upper);
  }

  cross(o, a, b) {
    return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  }

  computeMinBoundingRectangle(points) {
    if (points.length === 0) return null;
    
    // 转换为Vector2
    const vec2Points = points.map(p => new THREE.Vector2(p[0], p[2]));
    if (points.length === 1) {
        // 单个点：返回一个微小矩形
        const [x, y] = points[0];
        const size = 0.1;
        return [
            [x - size, y - size],
            [x + size, y - size],
            [x + size, y + size],
            [x - size, y + size]
        ];
    }
    
    const hull = this.convexHull(vec2Points);
    const n = hull.length;
    
    let minArea = Infinity;
    let bestRect = null;
    
    // 遍历凸包每条边
    for (let i = 0, j = n - 1; i < n; j = i, i++) {
        const edge = new THREE.Vector2().subVectors(hull[i], hull[j]);
        const axis = edge.clone().normalize();
        const normal = new THREE.Vector2(-axis.y, axis.x); // 垂直方向
        
        // 计算投影范围
        let minProj = Infinity, maxProj = -Infinity;
        let minNormal = Infinity, maxNormal = -Infinity;
        
        for (const p of hull) {
            const v = new THREE.Vector2().subVectors(p, hull[j]);
            const proj = v.dot(axis);
            const projNormal = v.dot(normal);
            
            minProj = Math.min(minProj, proj);
            maxProj = Math.max(maxProj, proj);
            minNormal = Math.min(minNormal, projNormal);
            maxNormal = Math.max(maxNormal, projNormal);
        }
        
        // 计算矩形面积
        const width = maxProj - minProj;
        const height = maxNormal - minNormal;
        const area = width * height;
        
        // 更新最小矩形
        if (area < minArea) {
            minArea = area;
            
            // 计算矩形四个角点
            const origin = new THREE.Vector2().copy(hull[j])
                .add(axis.clone().multiplyScalar(minProj))
                .add(normal.clone().multiplyScalar(minNormal));
            
            // 创建矩形的四个顶点（顺时针顺序）
            const rect = [
                origin.clone(),
                origin.clone().add(axis.clone().multiplyScalar(width)),
                origin.clone().add(axis.clone().multiplyScalar(width))
                    .add(normal.clone().multiplyScalar(height)),
                origin.clone().add(normal.clone().multiplyScalar(height))
            ];
            // 转换为普通数组格式
            bestRect = rect.map(p => [p.x, p.y]);
        }
    }
    return bestRect;
  }

  //返回的是顺时针
  orderRectangleVertices(points) {
    if (points.length !== 4) return points;
    
    // 1. 复制数组避免修改原数据
    const sortedPoints = [...points];
    
    // 2. 按x坐标排序（从小到大）
    sortedPoints.sort((a, b) => a[0] - b[0]);
    
    
    // 3. 取x最小的两个点
    const minXPoints = [sortedPoints[0], sortedPoints[1]];
    // 4. 在这两个点中取z值较小的点作为起点
    const startPoint = minXPoints[0][1] < minXPoints[1][1] 
        ? minXPoints[0] 
        : minXPoints[1];
    // 5. 找到起点在原始数组中的位置
    const startIndex = points.findIndex(p => 
        p[0] === startPoint[0] && p[1] === startPoint[1]
    );
    
    // 6. 重新排序数组（从起点开始）
    const reordered = [];
    for (let i = 0; i < 4; i++) {
        reordered.push(points[(startIndex + i) % 4]);
    }
    return reordered;
  }

  createBuildingFloorByHouse(buildGroup, buildingNumber, houseRoom,points, floorHeight, index, baseHeight) {
    let finalBaseHeight = 0
    if (this.minBaseHeight < 0) {
      finalBaseHeight = floorHeight * index + baseHeight - this.minBaseHeight
    } else {
      finalBaseHeight = floorHeight * index + baseHeight
    }
    const geometry = this.getGeometry(points, finalBaseHeight, floorHeight)
    const materialArr =
      new THREE.MeshLambertMaterial({
        // color: 0xB60707,
        color: 0xE8E8E8,
        emissive: 0xE8E8E8, // 可选：设置自发光颜色，让材质在没有光照时也能有一定的亮度  
        emissiveIntensity: 0.6,
        side: THREE.BackSide,
        depthTest: true,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      })
    const mesh = new THREE.Mesh(geometry, materialArr)
    mesh.castShadow = true
    mesh.receiveShadow = true
    mesh.name = `Room_${buildingNumber}_${houseRoom.unit}_${Number(index + 1)}_${houseRoom.room}_sold` 
    buildGroup.add(mesh)
    let roomKey = buildingNumber + "_" +  houseRoom.unit + "_" + Number(index + 1) + "_" + houseRoom.room
    this.roomMeshMap.set(roomKey, mesh)
    // 创建边缘几何体
    const edgeGeometry = new THREE.EdgesGeometry(geometry, 1);
    const edgeMaterial = new THREE.LineBasicMaterial({
      color: 0x2C216D,
      depthTest: true,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: 3,
    });

    const edgeLines = new THREE.LineSegments(edgeGeometry, edgeMaterial);
    edgeLines.renderOrder = 1;
    buildGroup.add(edgeLines)
  }

  createBuildingFloorByBuilding(buildGroup, points, floorHeight, index, baseHeight) {

    let finalBaseHeight = 0
    if (this.minBaseHeight < 0) {
      finalBaseHeight = floorHeight * index + baseHeight - this.minBaseHeight
    } else {
      finalBaseHeight = floorHeight * index + baseHeight
    }
    const geometry = this.getGeometry(points, finalBaseHeight, floorHeight)
    const materialArr =
      new THREE.MeshLambertMaterial({
        // color: 0xB60707,
        color: 0xffffff,
        emissive: 0xffffff, // 可选：设置自发光颜色，让材质在没有光照时也能有一定的亮度  
        emissiveIntensity: 0.6,
        side: THREE.BackSide,
        depthTest: true,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      })
    const mesh = new THREE.Mesh(geometry, materialArr)
    mesh.castShadow = true
    mesh.receiveShadow = true
    buildGroup.add(mesh)

    // 创建边缘几何体
    const edgeGeometry = new THREE.EdgesGeometry(geometry, 1);
    const edgeMaterial = new THREE.LineBasicMaterial({
      color: 0x2C216D,
      depthTest: true,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: 3,
    });

    const edgeLines = new THREE.LineSegments(edgeGeometry, edgeMaterial);
    edgeLines.renderOrder = 1;
    buildGroup.add(edgeLines)
  }

  getGeometry(points, baseHeight, floorHeight){
    var topPoints = []
    let basePoints = JSON.parse(JSON.stringify(points))

    for (let i = 0; i < basePoints.length; i++) {
      basePoints[i][1] = baseHeight
      topPoints.push([basePoints[i][0], baseHeight + floorHeight, basePoints[i][2]])
    }
    var totalPoints = basePoints.concat(topPoints)
    var vertices = []
    for (let i = 0; i < totalPoints.length; i++) {
      vertices.push(
        new THREE.Vector3(totalPoints[i][0], totalPoints[i][1], totalPoints[i][2])
      )
    }
    var length = points.length
    var faces = []
    //侧面生成三角形
    for (let j = 0; j < length; j++) {
      if (j != length - 1) {
        faces.push(new THREE.Face3(j, j + 1, length + j + 1))
        faces.push(new THREE.Face3(length + j + 1, length + j, j))
      } else {
        faces.push(new THREE.Face3(j, 0, length))
        faces.push(new THREE.Face3(length, length + j, j))
      }
    }
    var data = []
    for (let i = 0; i < length; i++) {
      data.push(points[i][0], points[i][2])
    }
    var triangles = Earcut.triangulate(data);
    if (triangles && triangles.length != 0) {
      for (let i = 0; i < triangles.length; i++) {
        var tlength = triangles.length
        if (i % 3 == 0 && i < tlength - 2) {
          //底部的三角面
          let face1 = new THREE.Face3(
            triangles[i],
            triangles[i + 1],
            triangles[i + 2]
          )
          //顶部的三角面
          let face2 = new THREE.Face3(
            triangles[i] + length,
            triangles[i + 1] + length,
            triangles[i + 2] + length
          )
          //纹理编号
          face1.materialIndex = 1
          face2.materialIndex = 1
          faces.push(face1)
          faces.push(face2)
        }
      }
    }
    var geometry = new THREE.Geometry()
    geometry.vertices = vertices
    geometry.faces = faces
    geometry.computeFaceNormals() //自动计算法向量

    return geometry
  }

  transFormPositions(destPositions) {
    let coordinates = destPositions.map(destPosition => {
      let newRing = [];
      newRing.push(destPosition.lng);
      newRing.push(destPosition.lat);
      return newRing;
    })
    return coordinates;
  }

  lonlat2WebMercator(lon, lat) {
    let xy = []
    let x = (lon * 20037508.34) / 180
    let y = Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180)
    y = (y * 20037508.34) / 180
    xy[0] = x
    xy[1] = y
    return xy
  }
  degreesToRadians(degrees) {
    return degrees * (Math.PI / 180);
  }
  getBuildingText(width,height,text) { 
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // 设置Canvas尺寸（建议2的幂）
    canvas.width = width * 10;
    canvas.height = height * 10;

    // 动态计算字体大小
    let fontSize = Math.min(canvas.width / 2, canvas.height / 2);
    while (fontSize > 1) {
      ctx.font = `bold ${fontSize}px Arial`;
      const metrics = ctx.measureText(text);
      const textWidth = metrics.width;
      if (textWidth <= canvas.width - 20) {
        break;
      }
      fontSize--;
    }
    // 清除为完全透明
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // 绘制文字
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.fillStyle = 'black';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    const texture = new THREE.CanvasTexture(canvas);
    texture.premultiplyAlpha = false; // 通常设为 false
    texture.format = THREE.RGBAFormat; // 明确使用 RGBA 格式

    return texture; // 关键：转换为纹理
  }

  switchHighlightBuilding (number) {
    let encodeNumber = encodeURIComponent(number).replace('-', '%2D')
    if (number === undefined) {
      this.resetHightBuilding(this.buildingGroup, this.selectBuildGroup)
    }
    else {
      var selectBuild = this.buildingNumberMap.get(encodeNumber)
      if(selectBuild){
        this.resetHightBuilding(this.buildingGroup, this.selectBuildGroup)
        this.highlightBuilding(this.buildingGroup, selectBuild)
      }
    }
  }

  removeMeshGroup(group){
    if (!group) return;
    this._scene.remove(group);
    group.traverse(child => {
      if (child.geometry) {
        child.geometry.dispose();
        child.geometry = null;
      }
      // 材质
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(this.disposeMaterial);
        } else {
          this.disposeMaterial(child.material);
        }
        child.material = null;
      }
      // 纹理和其他资源
      if (child.texture) {
        child.texture.dispose();
        child.texture = null;
      }
      // 自定义资源
      if (typeof child.dispose === 'function') {
        child.dispose();
      }
    });
    while(group.children.length > 0) {
      group.remove(group.children[0]);
    }
    group = null;
  }

  disposeMaterial(material) {
    material.dispose();
    
    // 释放材质关联的纹理
    const textureKeys = [
      'map', 'alphaMap', 'emissiveMap', 'bumpMap', 
      'normalMap', 'displacementMap', 'roughnessMap',
      'metalnessMap', 'envMap'
    ];
    
    textureKeys.forEach(key => {
      if (material[key]) {
        material[key].dispose();
        material[key] = null;
      }
    });
  }

  initSellControl() {
    this._sellControlMap.forEach((controlList, buildingNumber) => {
      controlList.forEach((sellControlData)=>{
        let roomKey = `${buildingNumber}_${sellControlData.unit}_${sellControlData.floor}_${sellControlData.room}`
        let roomMesh = this.roomMeshMap.get(roomKey)
        const soldStatus = sellControlData.soldStatus
        if(roomMesh){
          if (soldStatus == 0) {
            roomMesh.name = roomMesh.name.substring(0, roomMesh.name.lastIndexOf('_')) + '_forSale'
            this.roomMeshMap.set(roomKey, roomMesh)
            this.updateRoomColor(roomMesh, 'forSale')
          }
          if (soldStatus == 1) {
            roomMesh.name = roomMesh.name.substring(0, roomMesh.name.lastIndexOf('_')) + '_sold'
            this.roomMeshMap.set(roomKey, roomMesh)
            this.updateRoomColor(roomMesh, 'sold')
          }
          if (soldStatus == 2) {
            roomMesh.name = roomMesh.name.substring(0, roomMesh.name.lastIndexOf('_')) + '_discount'
            this.roomMeshMap.set(roomKey, roomMesh)
            this.updateRoomColor(roomMesh, 'discount')
          }
        }
      })
    })
  }

  updateRoomColor(mesh, Type) {
    let color = 0xffffff;
    if (Type == 'forSale') {
      color = 0xC5EEFF
    } else if(Type == 'sold'){
      color = 0xE8E8E8
    } else if(Type == 'discount'){
      color = 0xF8CDEB
    } else if (Type == 'select'){
      color = 0xFF7522
    } else {
      color = 0xA293F3
    }
    mesh.material.color.setHex(color)
    mesh.material.emissive.setHex(color)
  }
}